import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./Modal.css";

interface ModalProps {
    open: boolean;
    onClose?: () => void;
    title: string;
    children: ReactNode;
    actionLabel?: string;
    onAction?: () => void;
    panelClassName?: string;
}

/**
 * Universal popup. Mount it once and control it with an `open` boolean:
 *
 *   const [open, setOpen] = useState(false);
 *   <Modal open={open} onClose={() => setOpen(false)} title="Tree saved">
 *     Your changes have been saved.
 *   </Modal>
 *
 * Animates in/out, closes on backdrop click, close-button click, or Escape.
 */
export default function Modal({
                                  open,
                                  onClose,
                                  title,
                                  children,
                                  actionLabel,
                                  onAction,
                                  panelClassName = "",
                              }: ModalProps) {
    const [shouldRender, setShouldRender] = useState(open);
    const [closing, setClosing] = useState(false);

    useEffect(() => {
        if (open) {
            setShouldRender(true);
            setClosing(false);
        } else if (shouldRender) {
            setClosing(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [open, onClose]);

    if (!shouldRender) return null;

    const handleOverlayAnimationEnd = () => {
        if (closing) {
            setShouldRender(false);
            setClosing(false);
        }
    };

    // Portaled straight to <body> so a `position: fixed` overlay always
    // covers the real viewport — otherwise any ancestor with so much as a
    // finished, forwards-filled CSS animation on `transform` (e.g. the
    // mobile drawer sliding in) silently becomes its containing block and
    // traps the "fixed" overlay inside that ancestor's box instead.
    return createPortal(
        <div
            className={`modal-overlay ${closing ? "modal-overlay--closing" : ""}`}
            onClick={onClose}
            onAnimationEnd={handleOverlayAnimationEnd}
        >
            <div
                className={`modal-panel ${panelClassName} ${closing ? "modal-panel--closing" : ""}`.trim()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-panel-title"
                onClick={(e) => e.stopPropagation()}
            >
                {onClose &&
                    <button className="modal-panel__close" onClick={onClose} aria-label="Close">
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                            <path
                                d="M6 6 L18 18 M18 6 L6 18"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                }

                {/*<span className="eyebrow modal-panel__eyebrow">Notice</span>*/}
                <h2 id="modal-panel-title" className="modal-panel__title">
                    {title}
                </h2>
                <div className="modal-panel__body">{children}</div>

                {actionLabel && (
                    <button className="modal-panel__action" onClick={onAction ?? onClose}>
                        {actionLabel}
                    </button>
                )}
            </div>
        </div>,
        document.body
    );
}